import React, { useRef, useEffect, useState } from "react";
import { Combobox, ComboboxButton, ComboboxInput, ComboboxOption, ComboboxOptions } from "@headlessui/react";
import CheckIcon from "@icons/check-circle.svg?react";
import ChevronDownIcon from "@icons/chevron-down.svg?react";
import XCloseIcon from "@icons/x-close.svg?react";
import classNames from "classnames";
import { Tooltip } from "react-tooltip";

type HDSingleSelectSharedProps = {
    options: string[];
    value: string | null;
    disabled?: boolean;
    customClassNames?: string;
    placeholder?: string;
    id?: string;
    icon?: React.FunctionComponent<React.SVGProps<SVGSVGElement>>;
    hasTooltip?: boolean;
    useSearch?: boolean;
    /** Optional function to get display label for an option value */
    getOptionLabel?: (value: string) => string;
    /** Extra class names for the combobox input (padding, ring-0 for borderless, etc.). Defaults to py-2. */
    inputClassName?: string;
    /** How to match options against the search query when useSearch=true.
     *  "startsWith" (default) preserves existing behavior; "includes" matches any substring. */
    filterMode?: "startsWith" | "includes";
};

export type HDSingleSelectProps =
    | (HDSingleSelectSharedProps & {
          nullOption: string;
          onChange: (value: string | null) => void;
      })
    | (HDSingleSelectSharedProps & {
          nullOption?: null;
          onChange: (value: string) => void;
      });

function HDSingleSelect({
    options,
    value,
    onChange,
    disabled = false,
    customClassNames = "",
    placeholder = "Select...",
    id = "hd-single-select",
    icon,
    hasTooltip = false,
    useSearch = false, // pass useSearch=true to enable search on the select
    getOptionLabel,
    inputClassName = "py-1.5",
    filterMode = "startsWith",
    nullOption,
}: HDSingleSelectProps) {
    const [query, setQuery] = React.useState("");

    // Helper to get display label for an option
    const getLabel = (option: string) => (getOptionLabel ? getOptionLabel(option) : option);

    const listboxButtonLabel = `${placeholder}: open options`;

    // Filter options based on the search query. Match against BOTH the display label and the
    // underlying option value, so options whose label differs from their value (e.g. timezone
    // ids shown with friendly labels) are still findable by typing the value (e.g. "Toronto").
    const filteredOptions = useSearch
        ? options.filter((option) => {
              const q = query.toLowerCase();
              const label = getLabel(option).toLowerCase();
              const value = option.toLowerCase();
              return filterMode === "includes"
                  ? label.includes(q) || value.includes(q)
                  : label.startsWith(q) || value.startsWith(q);
          })
        : options;

    const isSelected = (option: string) => {
        return value === option;
    };

    // this function checks if the element has a scrollable parent
    const getScrollParent = (element: HTMLElement | null): HTMLElement | null => {
        if (!element) return null;
        const overflowY = window.getComputedStyle(element).overflowY;
        const isScrollable = overflowY !== "visible" && overflowY !== "hidden";
        if (isScrollable && element.scrollHeight > element.clientHeight) {
            return element;
        }
        return getScrollParent(element.parentElement);
    };

    const wrapperRef = useRef<HTMLDivElement>(null);
    const inputRef = useRef<HTMLInputElement>(null);
    const [anchor, setAnchor] = useState<"top" | "bottom">("bottom");

    // Calculate anchor position based on available space
    const updateAnchor = () => {
        if (wrapperRef.current) {
            const rect = wrapperRef.current.getBoundingClientRect();
            const spaceBelow = window.innerHeight - rect.bottom;
            const spaceAbove = rect.top;

            // Estimate dropdown height (max-h-60 = 240px)
            const estimatedHeight = 240;

            // Position above if not enough space below and more space above
            if (spaceBelow < estimatedHeight && spaceAbove > spaceBelow) {
                setAnchor("top");
            } else {
                setAnchor("bottom");
            }
        }
    };

    // Update anchor when options change or window resizes
    useEffect(() => {
        updateAnchor();

        const scrollParent = getScrollParent(wrapperRef.current);

        // Named handler function so it can be properly removed in cleanup
        const handleScroll = () => {
            if (inputRef.current) {
                inputRef.current.blur();
            }
        };

        scrollParent?.addEventListener("scroll", handleScroll);
        window.addEventListener("resize", updateAnchor);

        return () => {
            scrollParent?.removeEventListener("scroll", handleScroll);
            window.removeEventListener("resize", updateAnchor);
        };
    }, [filteredOptions.length]);

    const handleComboboxChange = (newValue: string | null) => {
        if (nullOption) {
            (onChange as (value: string | null) => void)(newValue);
        } else if (newValue !== null) {
            (onChange as (value: string) => void)(newValue);
        }
    };

    return (
        <Combobox
            as="div"
            value={value}
            onChange={handleComboboxChange}
            disabled={disabled}
            className={customClassNames}
            id={id}
            key={id}
            name="hd-single-select"
        >
            {/* eslint-disable-next-line jsx-a11y/no-static-element-interactions, jsx-a11y/click-events-have-key-events -- positioning wrapper whose onClick only stops propagation; keyboard handled by the inner ComboboxInput/ComboboxButton */}
            <div
                className="relative h-full"
                ref={wrapperRef}
                onClick={(e) => {
                    e.stopPropagation();
                    updateAnchor();
                }}
            >
                <div className="relative h-full">
                    {icon && (
                        <div className="absolute inset-y-0 left-0 flex items-center pl-2">
                            {/* Render the passed icon component */}
                            {React.createElement(icon, { className: "h-5 w-5", "aria-hidden": "true" })}
                        </div>
                    )}
                    <Tooltip
                        style={{ maxWidth: "200px", zIndex: 9999, fontSize: "12px", fontFamily: "Plus Jakarta Sans" }}
                        anchorSelect={`#hd-single-select-info-icon`}
                        content={value ? getLabel(value) : ""}
                        place="left"
                        disableTooltip={() => !hasTooltip}
                    />
                    <ComboboxInput
                        ref={inputRef}
                        className={classNames(
                            disabled ? "text-gray-400 placeholder:text-gray-400" : "text-gray-900",
                            icon ? "pl-8" : "pl-3",
                            "w-full h-full py-[8px] rounded-md border-0 ring-1 ring-inset ring-gray-300 bg-white pr-10 shadow-sm text-xs leading-tight sm:text-sm sm:leading-5 focused-inset",
                            inputClassName,
                        )}
                        id={`hd-single-select-info-icon`}
                        displayValue={(option: string) => (option ? getLabel(option) : "")}
                        placeholder={placeholder}
                        data-testid="string-select"
                        readOnly={!useSearch}
                        onChange={(e) => setQuery(e.target.value)}
                    />
                    <ComboboxButton className="absolute inset-0" aria-label={listboxButtonLabel} />
                </div>

                <ComboboxButton
                    className="absolute inset-y-0 right-0 flex items-center rounded-r-md px-2 focus:outline-none focus-visible:border-max-700"
                    aria-label={listboxButtonLabel}
                >
                    <ChevronDownIcon className="h-5 w-5 text-gray-400" aria-hidden="true" />
                </ComboboxButton>
                {filteredOptions.length > 0 && (
                    <ComboboxOptions
                        anchor={anchor}
                        className="text-start max-h-60 overflow-auto rounded-md bg-white py-1 text-base shadow-lg ring-1 ring-gray-900 ring-opacity-5 focus:outline-none sm:text-sm z-[1000]"
                        style={{ width: "var(--input-width)" }}
                    >
                        {nullOption && (
                            <ComboboxOption
                                as="button"
                                type="button"
                                value={null}
                                className={({ focus }) =>
                                    classNames(
                                        "w-full flex relative cursor-default select-none py-2 pl-3 pr-9 border-b border-gray-100",
                                        focus ? "bg-max-900 text-white" : "text-gray-600",
                                    )
                                }
                            >
                                <span className="flex items-center gap-2">
                                    <XCloseIcon className="h-4 w-4 shrink-0" aria-hidden focusable={false} />
                                    <span>{nullOption}</span>
                                </span>
                            </ComboboxOption>
                        )}
                        {filteredOptions.map((option) => (
                            <ComboboxOption
                                as="button"
                                key={option}
                                value={option}
                                className={({ focus }) =>
                                    classNames(
                                        "w-full flex relative cursor-default select-none py-2 pl-3 pr-9",
                                        focus ? "bg-max-900 text-white" : "text-gray-900",
                                    )
                                }
                            >
                                {({ focus }) => (
                                    <>
                                        <span
                                            className={classNames(
                                                "block truncate",
                                                isSelected(option) && "font-semibold",
                                            )}
                                        >
                                            {getLabel(option)}
                                        </span>
                                        {isSelected(option) && (
                                            <span
                                                className={classNames(
                                                    "absolute inset-y-0 right-0 flex items-center pr-4",
                                                    focus ? "text-white" : "text-max-900",
                                                )}
                                            >
                                                <CheckIcon className="h-5 w-5" aria-hidden="true" />
                                            </span>
                                        )}
                                    </>
                                )}
                            </ComboboxOption>
                        ))}
                    </ComboboxOptions>
                )}
            </div>
        </Combobox>
    );
}

export default HDSingleSelect;
